import { Currency } from "client/api-v2";
import type { PricingVector, RateCardTermsDto } from "client/api-v2";

/**
 * Numeric inputs are held as strings so the user can type freely (partial numbers,
 * a lone "."), and are parsed once on submit.
 */
export interface CreateRateCardFormValues {
  label: string;
  unitPrices: { vector: PricingVector; listPricePerUnit: string }[];
  discountExponent: string;
  discountThresholdUnits: string;
  /** Entered as a percentage; `toRateCardTerms` converts it to the fraction the API takes. */
  overallDiscountPercent: string;
  finalPriceRoundDownToNearest: string;
  fixedFees: { description: string; amount: string }[];
  reason: string;
}

// Plain decimal notation only. Scientific notation ("1e-3") would defeat the
// decimal-place count below, so it fails the must-be-a-number check instead.
const plainDecimalPattern = /^-?(\d+(\.\d*)?|\.\d+)$/;

export interface DecimalStringRules {
  /** Message when the value is blank; omit for optional fields, where blank is valid. */
  required?: string;
  mustBeNumber: string;
  negative: string;
  maxDecimals: number;
  overPrecise: string;
  /** Inclusive upper bound (e.g. the discount exponent's 1). */
  max?: { value: number; message: string };
}

/**
 * Validates a free-text decimal input: presence, plain decimal format, sign, scale,
 * and an optional upper bound. Returns the first failing message, or true.
 *
 * Over-precise values are rejected with a message rather than rounded, matching
 * `RateCardTerms.Validator`'s PrecisionScale stance: never silently alter a number
 * the caller supplied. Unit prices and fixed fees are JSON-stored, so the backend
 * preserves whatever scale it is sent — this form is where the cent-precision money
 * convention is enforced.
 *
 * Messages are supplied per field because the subjects differ grammatically
 * ("Unit prices are…" vs "Discount exponent is…").
 * @param value
 * @param rules
 */
export function validateDecimalString(value: string, rules: DecimalStringRules): string | true {
  const trimmed = value.trim();
  if (trimmed === "") {
    return rules.required ?? true;
  }
  if (!plainDecimalPattern.test(trimmed)) {
    return rules.mustBeNumber;
  }
  if (Number(trimmed) < 0) {
    return rules.negative;
  }
  const decimals = trimmed.split(".")[1]?.length ?? 0;
  if (decimals > rules.maxDecimals) {
    return rules.overPrecise;
  }
  if (rules.max !== undefined && Number(trimmed) > rules.max.value) {
    return rules.max.message;
  }
  return true;
}

/**
 * Converts an entered percentage to the fraction the API stores. The `toFixed` is not
 * cosmetic: dividing by 100 in binary floating point can leave an artifact several
 * places down, and the column is decimal(6,4) — the form caps the percentage at 2
 * decimal places, so 4 is exactly the scale the result needs.
 * @param percent
 */
function percentToFraction(percent: string): number {
  return Number((Number(percent) / 100).toFixed(4));
}

/**
 * The inverse, for a form seeded from a card that already exists. Rounded to the 2 places the
 * field accepts, because a stored 0.075 multiplies out to 7.500000000000001.
 * @param fraction
 */
export function fractionToPercent(fraction: number): string {
  return String(Number((fraction * 100).toFixed(2)));
}

export function toRateCardTerms(values: CreateRateCardFormValues): RateCardTermsDto {
  return {
    label: values.label.trim() === "" ? null : values.label.trim(),
    currency: Currency.USD,
    unitPrices: values.unitPrices.map((p) => ({
      vector: p.vector,
      listPricePerUnit: Number(p.listPricePerUnit),
    })),
    discountExponent: Number(values.discountExponent),
    discountThresholdUnits: Number(values.discountThresholdUnits),
    overallDiscountFraction: percentToFraction(values.overallDiscountPercent),
    fixedFees: values.fixedFees.map((f) => ({
      description: f.description.trim(),
      amount: Number(f.amount),
    })),
    finalPriceRoundDownToNearest:
      values.finalPriceRoundDownToNearest.trim() === "" ? null : Number(values.finalPriceRoundDownToNearest),
  };
}

/**
 * Flattens react-hook-form's nested FieldErrors into the flat list of messages shown
 * in the alert at the top of the dialog. `ref` nodes are skipped — they hold DOM
 * elements, which are cyclic.
 * @param errors
 */
export function collectErrorMessages(errors: unknown): string[] {
  const messages: string[] = [];
  const walk = (node: unknown) => {
    if (!node || typeof node !== "object") {
      return;
    }
    for (const [key, value] of Object.entries(node)) {
      if (key === "message" && typeof value === "string" && value !== "") {
        messages.push(value);
      } else if (key !== "ref" && key !== "message") {
        walk(value);
      }
    }
  };
  walk(errors);
  return [...new Set(messages)];
}
